import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import TopNav from '../components/TopNav';
import BottomNav from '../components/BottomNav';
import CustomerCreditPanel from '../components/CustomerCreditPanel';

const Profile = ({ deviceId }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [activeTab, setActiveTab] = useState('orders'); // 'orders', 'wishlist', 'info'
  const [loading, setLoading] = useState(true);

  const loadUserData = useCallback(async () => {
    try {
      // Get user from localStorage
      const storedUser = localStorage.getItem('loop_user');
      if (!storedUser) {
        // Not logged in, redirect to home
        navigate('/');
        return;
      }

      const userData = JSON.parse(storedUser);
      setUser(userData);

      // Get full profile with orders
      const profile = await api.getUserProfile(userData.email);
      setUserProfile(profile);

      // Get wishlist
      const wishlistData = await api.getUserWishlist(userData.email);
      setWishlist(wishlistData);

    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleRemoveFromWishlist = async (productId) => {
    try {
      await api.removeFromWishlist(user.email, productId);
      // Reload wishlist
      const wishlistData = await api.getUserWishlist(user.email);
      setWishlist(wishlistData);
    } catch (error) {
      console.error('Error removing from wishlist:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('loop_user');
    navigate('/');
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#FEFEE3', padding: '20px' }}>
        <p style={{ textAlign: 'center', fontSize: '14px', color: '#4C956C' }}>LOADING PROFILE...</p>
      </div>
    );
  }

  if (!user || !userProfile) {
    return (
      <div style={{ minHeight: '100vh', background: '#FEFEE3', padding: '20px' }}>
        <p style={{ textAlign: 'center', fontSize: '14px', color: '#FF5A5F' }}>PROFILE NOT FOUND</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#FEFEE3', paddingBottom: '80px' }}>
      <TopNav deviceId={deviceId} />

      {/* Profile Header */}
      <div style={{ background: 'linear-gradient(180deg, #2C6E49 0%, #1E4B32 100%)', padding: '40px 20px', borderBottom: '4px solid #000' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '80px', height: '80px', background: '#FFC857', border: '4px solid #000', borderRadius: '50%', margin: '0 auto 15px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '4px 4px 0 0 #000' }}>
              <span style={{ fontSize: '36px' }}>👤</span>
            </div>
            <h1 style={{ fontSize: '24px', color: '#FEFEE3', marginBottom: '8px', letterSpacing: '0.1em' }}>{user.full_name.toUpperCase()}</h1>
            <p style={{ fontSize: '12px', color: '#4AE54A' }}>{user.email}</p>
            <p style={{ fontSize: '11px', color: '#FEFEE3', marginTop: '10px' }}>
              MEMBER SINCE {new Date(userProfile.created_at).toLocaleDateString()}
            </p>
          </div>
          
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginTop: '30px' }}>
            <div style={{ background: '#000', padding: '15px', border: '3px solid #4AE54A', textAlign: 'center', boxShadow: '0 0 10px rgba(74, 229, 74, 0.3)' }}>
              <p style={{ fontSize: '10px', color: '#4AE54A', marginBottom: '5px' }}>ORDERS</p>
              <p style={{ fontSize: '24px', color: '#FEFEE3', fontWeight: 'bold' }}>{userProfile.total_orders}</p>
            </div>
            <div style={{ background: '#000', padding: '15px', border: '3px solid #FFC857', textAlign: 'center', boxShadow: '0 0 10px rgba(255, 200, 87, 0.3)' }}>
              <p style={{ fontSize: '10px', color: '#FFC857', marginBottom: '5px' }}>TOTAL SPENT</p>
              <p style={{ fontSize: '20px', color: '#FEFEE3', fontWeight: 'bold', fontFamily: 'IBM Plex Mono, monospace' }}>${userProfile.total_spent.toFixed(2)}</p>
            </div>
            <div style={{ background: '#000', padding: '15px', border: '3px solid #FF5A5F', textAlign: 'center', boxShadow: '0 0 10px rgba(255, 90, 95, 0.3)' }}>
              <p style={{ fontSize: '10px', color: '#FF5A5F', marginBottom: '5px' }}>WISHLIST</p>
              <p style={{ fontSize: '24px', color: '#FEFEE3', fontWeight: 'bold' }}>{wishlist.length}</p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="retro-btn"
            style={{ background: '#FF5A5F', color: '#FFF', padding: '10px 20px', fontSize: '11px', marginTop: '20px', display: 'block', margin: '20px auto 0' }}
          >
            LOGOUT
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '4px solid #000', background: '#FFF' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex' }}>
          <button
            onClick={() => setActiveTab('orders')}
            style={{
              flex: 1,
              padding: '15px',
              background: activeTab === 'orders' ? '#2C6E49' : '#DDD',
              color: activeTab === 'orders' ? '#FEFEE3' : '#666',
              border: 'none',
              borderRight: '2px solid #000',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            📦 ORDERS
          </button>
          <button
            onClick={() => setActiveTab('wishlist')}
            style={{
              flex: 1,
              padding: '15px',
              background: activeTab === 'wishlist' ? '#2C6E49' : '#DDD',
              color: activeTab === 'wishlist' ? '#FEFEE3' : '#666',
              border: 'none',
              borderRight: '2px solid #000',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            ❤️ WISHLIST
          </button>
          <button
            onClick={() => setActiveTab('credit')}
            data-testid="profile-credit-tab"
            style={{
              flex: 1,
              padding: '15px',
              background: activeTab === 'credit' ? '#2C6E49' : '#DDD',
              color: activeTab === 'credit' ? '#FEFEE3' : '#666',
              border: 'none',
              borderRight: '2px solid #000',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            💰 CREDIT
          </button>
          <button
            onClick={() => setActiveTab('info')}
            style={{
              flex: 1,
              padding: '15px',
              background: activeTab === 'info' ? '#2C6E49' : '#DDD',
              color: activeTab === 'info' ? '#FEFEE3' : '#666',
              border: 'none',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            ℹ️ INFO
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 20px' }}>
        
        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <h2 style={{ fontSize: '18px', color: '#2C6E49', marginBottom: '20px' }}>[ ORDER HISTORY ]</h2>
            
            {userProfile.orders && userProfile.orders.length > 0 ? (
              userProfile.orders.map((order) => (
                <div key={order._id} className="retro-card" style={{ background: '#FFF', padding: '20px', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <div>
                      <p style={{ fontSize: '11px', color: '#4C956C', marginBottom: '3px' }}>ORDER #{order._id.slice(-8).toUpperCase()}</p>
                      <p style={{ fontSize: '10px', color: '#999' }}>{new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '16px', color: '#2C2C2C', fontWeight: 'bold', fontFamily: 'IBM Plex Mono, monospace' }}>${order.total.toFixed(2)}</p>
                      <span style={{
                        background: order.payment_status === 'paid' ? '#2C6E49' : '#FFC857',
                        color: '#FFF',
                        padding: '3px 10px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        marginTop: '5px'
                      }}>
                        {order.payment_status?.toUpperCase() || 'PENDING'}
                      </span>
                    </div>
                  </div>
                  
                  <div style={{ borderTop: '2px solid #F7F6D8', paddingTop: '15px' }}>
                    {order.items?.map((item) => (
                      <div key={`${order._id}-${item.name}-${item.color}-${item.size}`} style={{ marginBottom: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#2C2C2C', flex: 1 }}>
                          {item.name} ({item.color}, {item.size}) × {item.quantity}
                        </div>
                        <div style={{ fontSize: '11px', color: '#4C956C', fontFamily: 'IBM Plex Mono, monospace' }}>
                          ${item.price.toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <p style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>NO ORDERS YET</p>
            )}
          </div>
        )}

        {/* Wishlist Tab */}
        {activeTab === 'wishlist' && (
          <div>
            <h2 style={{ fontSize: '18px', color: '#2C6E49', marginBottom: '20px' }}>[ MY WISHLIST ]</h2>
            
            {wishlist.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                {wishlist.map((product) => (
                  <div key={product._id} className="retro-card" style={{ background: '#FFF', padding: '15px', position: 'relative' }}>
                    <button
                      onClick={() => handleRemoveFromWishlist(product._id)}
                      className="retro-btn"
                      style={{ position: 'absolute', top: '10px', right: '10px', background: '#FF5A5F', color: '#FFF', padding: '5px 10px', fontSize: '10px', zIndex: 10 }}
                    >
                      ✕
                    </button>
                    
                    <div onClick={() => navigate(`/product/${product._id}`)} style={{ cursor: 'pointer' }}>
                      {product.images && product.images[0] && (
                        <img src={product.images[0]} alt={product.name} style={{ width: '100%', height: '200px', objectFit: 'cover', marginBottom: '10px', border: '3px solid #000' }} />
                      )}
                      <h3 style={{ fontSize: '12px', color: '#2C2C2C', marginBottom: '5px' }}>{product.name}</h3>
                      <p style={{ fontSize: '14px', color: '#2C6E49', fontWeight: 'bold', fontFamily: 'IBM Plex Mono, monospace' }}>${product.base_price.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>NO ITEMS IN WISHLIST</p>
            )}
          </div>
        )}

        {/* Credit Tab */}
        {activeTab === 'credit' && user?.email && (
          <div data-testid="profile-credit-content">
            <h2 style={{ fontSize: '18px', color: '#2C6E49', marginBottom: '20px' }}>[ STORE CREDIT ]</h2>
            <CustomerCreditPanel email={user.email} readonly />
            <div style={{
              background: '#FFF', padding: '14px 18px', border: '3px solid #000',
              boxShadow: '4px 4px 0 0 #000', marginTop: '20px',
              fontSize: '12px', color: '#2C2C2C', lineHeight: 1.6,
            }}>
              ★ Store credit is automatically applied at checkout. Just toggle <em>Use my $X.XX store credit</em> on the payment screen — it'll deduct from your order total before payment.
            </div>
          </div>
        )}

        {/* Info Tab */}
        {activeTab === 'info' && (
          <div>
            <h2 style={{ fontSize: '18px', color: '#2C6E49', marginBottom: '20px' }}>[ ACCOUNT INFO ]</h2>
            
            <div className="retro-card" style={{ background: '#FFF', padding: '20px', marginBottom: '20px' }}>
              <div style={{ marginBottom: '15px' }}>
                <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>FULL NAME</p>
                <p style={{ fontSize: '14px', color: '#2C2C2C' }}>{user.full_name}</p>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>EMAIL</p>
                <p style={{ fontSize: '14px', color: '#2C2C2C' }}>{user.email}</p>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>MEMBER SINCE</p>
                <p style={{ fontSize: '14px', color: '#2C2C2C' }}>{new Date(userProfile.created_at).toLocaleString()}</p>
              </div>
              <div>
                <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>LAST LOGIN</p>
                <p style={{ fontSize: '14px', color: '#2C2C2C' }}>{new Date(userProfile.last_login).toLocaleString()}</p>
              </div>
            </div>

            {userProfile.shipping_addresses && userProfile.shipping_addresses.length > 0 && (
              <div className="retro-card" style={{ background: '#FFF', padding: '20px' }}>
                <h3 style={{ fontSize: '14px', color: '#2C6E49', marginBottom: '15px' }}>[ SAVED ADDRESSES ]</h3>
                {userProfile.shipping_addresses.map((addr, idx) => (
                  <div key={`${addr.address || ''}-${addr.zip_code || ''}-${idx}`} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: idx < userProfile.shipping_addresses.length - 1 ? '2px solid #F7F6D8' : 'none' }}>
                    <p style={{ fontSize: '12px', color: '#2C2C2C' }}>{addr.address}</p>
                    <p style={{ fontSize: '11px', color: '#4C956C' }}>{addr.city}, {addr.state} {addr.zip_code}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <BottomNav deviceId={deviceId} />
    </div>
  );
};

export default Profile;
